import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import SummaryOrderTotalsRow from './SummaryOrderTotalsRow/SummaryOrderTotalsRow';
import stylesModule from '../styles.module.scss';
import { ComponentLoader } from 'cat-ecommerce-alloy';
import { useSelector } from 'react-redux';
import {
  BILLING_METHOD_CAT_PREPAID_CERT,
  STATUS,
  CAT_CREDIT,
  BILLING_METHOD_NEW_CAT_CARD,
  BILLING_METHOD_CAT_CARD,
  PAGE_BREAKPOINTS
} from '@app/constants/commonConstants';
import { removeSpacesFromString } from '@app/utils/stringUtils';
import { priceDisplayFormat } from '@app/utils/paymentUtils';
import { Conditional } from '@cat-ecom/pcc-components';
import { useBreakpoint } from '@app/hooks';
import { replaceTokensInString } from '@app/utils';
import { PCC_PS_REPOSITIONING_UPDATES } from '@app/constants/featureFlags';
import useGetCatFinancialTranslations from '@app/hooks/useGetCatFinancialTranslations';

const SummaryOrderTotals = ({
  currency,
  billingMethod,
  additionalBillingMethod,
  subtotal,
  catCreditTotal,
  orderRemainingPaymentAmount,
  shippingCost,
  tax,
  taxLabel = [],
  total,
  orderAdjustment,
  discount,
  customLabelClassName = [],
  customValueClassName = [],
  chargedCustomClass = 'text-danger fw-bold',
  zeroFreightMessageEnabled = false,
  vatTaxEnabled = false,
  isOrderHistoryDetail = false,
  isOrderConfirmation = false,
  showBillingMethods = false,
  miscellaneous,
  miscellaneousItems,
  showInvoiceLabels = false,
  showOrderTotal = true,
  catCreditTotalUnformatted,
  catCreditsSalesforceAmount,
  catCardSalesforceAmount,
  salesforceCurrency,
  billingMethodValue
}) => {
  const [t] = useTranslation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const getAdjustmentAsNumber = val => {
    const parenthesisMultiplier = val.indexOf('(') > -1 ? -1 : 1;
    return parseFloat(val.replace(/[^0-9.-]+/g, '')) * parenthesisMultiplier;
  };
  const selectedBillingMethod = useSelector(
    s => s.checkout?.selectedBillingMethod
  );

  //Returning "Discount" if value returned is negative and "Order Adjustment" if value returned is positive
  const getOrderAdjustmentLabel = value =>
    getAdjustmentAsNumber(value) < 0
      ? t('MO_ORD_DISCOUNT')
      : t('MO_ORD_SHIPPING');
  const checkForOrderAdjustmentValue = value =>
    value && getAdjustmentAsNumber(value) !== 0;

  const printTextStyle = stylesModule['text-print-sm'];

  const commonLabelClasses = [
    'mb-0 order-total__label',
    printTextStyle,
    ...customLabelClassName
  ];
  const commonValueClasses = [
    'mb-0 order-total__value text-end',
    printTextStyle,
    ...customValueClassName
  ];

  const catCreditStatus = useSelector(
    state => state?.checkout?.prepaidCertificates?.catCreditStatus
  );
  const applyCatVantageRewardsToOrderStatus = useSelector(
    state => state?.checkout?.applyCatVantageRewardsToOrderStatus
  );
  const certificates = useSelector(
    state => state?.checkout?.prepaidCertificates?.certificates
  );

  const isCatCreditInternationalPhaseOne = useSelector(
    state => state.dealer?.isCreditsInternationalPhaseOneEnabled
  );

  const isCatCreditInternationalPhaseTwo = useSelector(
    state => state.dealer?.isCreditsInternationalPhaseTwoEnabled
  );

  const isCatCreditInternational =
    isCatCreditInternationalPhaseOne || isCatCreditInternationalPhaseTwo;

  const amountUsedCatCreditsGlobal = useSelector(
    s =>
      s.checkout?.applyCatCreditsGlobal?.applyCatCreditsGlobalBalance
        ?.amountUsedCatCreditsGlobal
  );

  const isCheckFreightLoading = useSelector(
    s => s?.checkout?.isCheckFreightLoading
  );

  const isLoading =
    catCreditStatus === STATUS.PENDING ||
    isCheckFreightLoading ||
    applyCatVantageRewardsToOrderStatus === STATUS.PENDING;

  const isIntlCatCreditsEnhancementsPhase1FlagEnabled = useSelector(
    state =>
      state.featureFlag?.PCC_InternationalCatCreditsTestingEnhancementsPhase1
  );

  const isCatCreditsEnhancementsPhase2Enabled = useSelector(
    s => s.featureFlag?.PCC_CatCreditsEnhancementsPhase2
  );

  const isCatFinancialEnhancementsPhase1Flag = useSelector(
    state => state.featureFlag?.PCC_CatFinancialEnhancementsPhase1
  );

  const isCatCreditBillingMethod =
    removeSpacesFromString(billingMethod) === BILLING_METHOD_CAT_PREPAID_CERT ||
    removeSpacesFromString(additionalBillingMethod) ===
      BILLING_METHOD_CAT_PREPAID_CERT ||
    (billingMethodValue === BILLING_METHOD_CAT_PREPAID_CERT &&
      isIntlCatCreditsEnhancementsPhase1FlagEnabled);

  const showCatCreditTotal =
    selectedBillingMethod?.value === BILLING_METHOD_CAT_PREPAID_CERT &&
    certificates?.length > 0;

  const unformattedRewardsAppliedToOrder = useSelector(
    state => state?.checkout?.orderTotals?.unformattedRewardsAppliedToOrder
  );
  const appliedRewardsByUserCVR = useSelector(
    s => s?.catVantage?.appliedRewards
  );

  const showCatCredit =
    amountUsedCatCreditsGlobal ||
    !!showCatCreditTotal ||
    (isOrderHistoryDetail &&
      isCatCreditBillingMethod &&
      catCreditTotalUnformatted) ||
    (isOrderConfirmation && showBillingMethods && catCreditTotalUnformatted) ||
    (isCatFinancialEnhancementsPhase1Flag
      ? unformattedRewardsAppliedToOrder
      : appliedRewardsByUserCVR);

  const isIntlCatCreditsEnhancementsFlagEnabled = useSelector(
    state => state.featureFlag?.PCC_InternationalCatCreditsTestingEnhancements
  );

  const isCVREnhancementsPhase2Enabled = useSelector(
    state => state.featureFlag?.PCC_CatVantageRewardsEnhancementsPhase2
  );

  const { catCreditsTranslation } = useGetCatFinancialTranslations();

  const appliedCatCredits = replaceTokensInString(
    t('APPLIED_CAT_CREDITS'),
    catCreditsTranslation
  );

  const catCreditIntlTitle =
    isIntlCatCreditsEnhancementsFlagEnabled ||
    isCatCreditInternational ||
    isCVREnhancementsPhase2Enabled
      ? appliedCatCredits
      : t('CAT_CREDITS_TOTAL');

  const isDualCurrencySupportFlagEnabled = useSelector(
    state => state.featureFlag?.PCC_DualCurrencySupportFlag
  );

  const isDualCurrencyUpdateForeignCurrencyNameEnabled = useSelector(
    state => state.featureFlag?.PCC_DualCurrencyUpdateForeignCurrencyName
  );

  const accountCurrencyCode = useSelector(
    s => s.checkout?.catSalesForceAccountBalance?.accountCurrencyCode
  );
  const accountCurrencyCodeSalesforceCurrency = useSelector(
    s =>
      s.checkout?.catSalesForceAccountBalance
        ?.accountCurrencyCodeSalesforceCurrency
  );
  const currencyCode = isDualCurrencyUpdateForeignCurrencyNameEnabled
    ? accountCurrencyCodeSalesforceCurrency
    : accountCurrencyCode;

  const globalAccountCurrencyCode = useSelector(
    s => s.checkout?.catCardGlobalOrderTotalResponse?.accountCurrencyCode
  );
  const globalAccountCurrencyCodeSalesforceCurrency = useSelector(
    s =>
      s.checkout?.catCardGlobalOrderTotalResponse
        ?.accountCurrencyCodeSalesforceCurrency
  );
  const orderTotalSecondaryCurrencyCode =
    isDualCurrencyUpdateForeignCurrencyNameEnabled
      ? globalAccountCurrencyCodeSalesforceCurrency
      : globalAccountCurrencyCode;

  const applyCatCreditsGlobalBalance = useSelector(
    s => s.checkout?.applyCatCreditsGlobal?.applyCatCreditsGlobalBalance
  );

  const orderTotalsForeignCurrency = useSelector(
    s => s.checkout?.catCardGlobalOrderTotalResponse?.orderTotalsForeignCurrency
  );
  const orderTotalsSalesforceCurrency = useSelector(
    s =>
      s.checkout?.catCardGlobalOrderTotalResponse?.orderTotalsSalesforceCurrency
  );
  const orderTotals = useSelector(
    s => s.checkout?.catCardGlobalOrderTotalResponse?.orderTotals
  );
  const secondaryOrderTotals = isDualCurrencyUpdateForeignCurrencyNameEnabled
    ? orderTotalsSalesforceCurrency
    : orderTotals;
  const secondaryOrderTotalsCurrencyExists =
    isDualCurrencyUpdateForeignCurrencyNameEnabled
      ? orderTotalsSalesforceCurrency
      : orderTotalsForeignCurrency;

  const showCatGlobalOrderTotal =
    isDualCurrencySupportFlagEnabled &&
    secondaryOrderTotalsCurrencyExists &&
    selectedBillingMethod.value?.includes(BILLING_METHOD_NEW_CAT_CARD);

  const remainingAmount =
    applyCatCreditsGlobalBalance?.remainingCatCreditsGlobal;

  const remainingAmountFixed =
    isDualCurrencySupportFlagEnabled && remainingAmount
      ? parseFloat(remainingAmount).toFixed(2)
      : null;

  const amountUsedCatCreditsGlobalFixed = amountUsedCatCreditsGlobal
    ? parseFloat(amountUsedCatCreditsGlobal).toFixed(2)
    : null;

  const shouldShowDualAppliedCredits =
    isDualCurrencyUpdateForeignCurrencyNameEnabled
      ? applyCatCreditsGlobalBalance?.usedAmountSalesforceCurrency
      : applyCatCreditsGlobalBalance?.usedAmountForeignCurrency;

  const amountUsedFixed =
    isDualCurrencyUpdateForeignCurrencyNameEnabled &&
    applyCatCreditsGlobalBalance?.usedAmountSalesforceCurrency
      ? amountUsedCatCreditsGlobalFixed
      : parseFloat(
          applyCatCreditsGlobalBalance?.usedAmountForeignCurrency
        ).toFixed(2);

  const secondaryAmountUsedFixed =
    isDualCurrencyUpdateForeignCurrencyNameEnabled &&
    applyCatCreditsGlobalBalance?.usedAmountSalesforceCurrency
      ? parseFloat(
          applyCatCreditsGlobalBalance?.usedAmountSalesforceCurrency
        ).toFixed(2)
      : amountUsedCatCreditsGlobalFixed;

  const catCreditsTotalFormatted = `${!isCatCreditsEnhancementsPhase2Enabled ? '-' : ''}- ${catCreditTotal ?? 0} ${currency}`;
  const amountUsedFormatted = `${isCatCreditsEnhancementsPhase2Enabled ? '-' : ''} $${amountUsedFixed} ${currency}`;

  const secondaryRemainingAmount =
    isDualCurrencyUpdateForeignCurrencyNameEnabled
      ? applyCatCreditsGlobalBalance?.remainingAmountSalesforceCurrency
      : applyCatCreditsGlobalBalance?.remainingAmountForeignCurrency;

  const remainingAmountToShow = isDualCurrencyUpdateForeignCurrencyNameEnabled
    ? remainingAmountFixed
    : applyCatCreditsGlobalBalance?.remainingAmountForeignCurrency;

  const secondaryRemainingAmountToShow =
    isDualCurrencyUpdateForeignCurrencyNameEnabled
      ? applyCatCreditsGlobalBalance?.remainingAmountSalesforceCurrency
      : remainingAmountFixed;

  const catCreditsLabel = `${
    isOrderHistoryDetail &&
    !isIntlCatCreditsEnhancementsFlagEnabled &&
    !isCVREnhancementsPhase2Enabled
      ? CAT_CREDIT
      : catCreditIntlTitle
  }:`;

  const isCatCardBillingMethod = billingMethod?.includes(
    BILLING_METHOD_CAT_CARD
  );

  const isBillingMethodDifferentToCatCreditsInOrderConfirmation =
    isOrderConfirmation && !!billingMethod && billingMethod !== CAT_CREDIT;

  const isBillingMethodDifferentToCatCreditsInOrderHistory =
    isOrderHistoryDetail &&
    !!billingMethodValue &&
    billingMethodValue !== BILLING_METHOD_CAT_PREPAID_CERT;

  const showBillingMethodDifferentToCatCredits =
    isIntlCatCreditsEnhancementsPhase1FlagEnabled
      ? (!!catCreditTotalUnformatted || isCatCardBillingMethod) &&
        (isBillingMethodDifferentToCatCreditsInOrderConfirmation ||
          isBillingMethodDifferentToCatCreditsInOrderHistory)
      : showCatCredit && !!billingMethod && billingMethod !== CAT_CREDIT;

  const repositioningPaymentSectionFlag = useSelector(
    s => s.featureFlag?.[PCC_PS_REPOSITIONING_UPDATES]
  );

  if (repositioningPaymentSectionFlag && !isMD) {
    return null;
  }

  return (
    <section
      className={cx('text-md')}
      data-testid="summary-order-totals-section"
    >
      {isLoading && <ComponentLoader />}
      <table className={cx('w-100 line-height-1')}>
        <tbody>
          <SummaryOrderTotalsRow
            extraValueElement={
              vatTaxEnabled ? (
                <span className="u-color--DarkGray fw-normal">*</span>
              ) : null
            }
            labelClasses={['text-uppercase', ...commonLabelClasses]}
            label={
              showInvoiceLabels
                ? `${t('INVOICE_SUBTOTAL')}:`
                : `${t('ORDER_SUB_TOTAL')}:`
            }
            valueClasses={commonValueClasses}
            value={priceDisplayFormat({ price: subtotal, currency })}
          />
          {checkForOrderAdjustmentValue(orderAdjustment) ? (
            <SummaryOrderTotalsRow
              labelClasses={commonLabelClasses}
              label={getOrderAdjustmentLabel(orderAdjustment)}
              valueClasses={commonValueClasses}
              value={priceDisplayFormat({ price: orderAdjustment, currency })}
            />
          ) : (
            discount && (
              <SummaryOrderTotalsRow
                labelClasses={commonLabelClasses}
                label={`${t('MO_ORD_DISCOUNT')}`}
                valueClasses={commonValueClasses}
                value={priceDisplayFormat({ price: discount, currency })}
              />
            )
          )}
          {!!shippingCost && (
            <SummaryOrderTotalsRow
              extraLabelElement={
                zeroFreightMessageEnabled ? (
                  <span
                    className={cx(
                      'u-color--DarkGray fw-normal',
                      printTextStyle
                    )}
                  >
                    *
                  </span>
                ) : null
              }
              labelClasses={commonLabelClasses}
              label={`${t('MA_OH_ORD_SHIPPING')}:`}
              valueClasses={commonValueClasses}
              value={priceDisplayFormat({ price: shippingCost, currency })}
            />
          )}
          {!!miscellaneous && (
            <SummaryOrderTotalsRow
              extraLabelElement={
                !!zeroFreightMessageEnabled && (
                  <span
                    className={cx(
                      'u-color--DarkGray fw-normal',
                      printTextStyle
                    )}
                  >
                    *
                  </span>
                )
              }
              labelClasses={commonLabelClasses}
              label={`${t('MISCELLANEOUS')}:`}
              valueClasses={commonValueClasses}
              value={priceDisplayFormat({ price: miscellaneous, currency })}
            />
          )}
          {!!miscellaneousItems &&
            miscellaneousItems.map(({ label, value }) => (
              <SummaryOrderTotalsRow
                key={label}
                labelClasses={['ps-4', ...commonLabelClasses]}
                label={label}
                valueClasses={commonValueClasses}
                value={priceDisplayFormat({ price: value, currency })}
              />
            ))}
          {tax && (
            <SummaryOrderTotalsRow
              labelClasses={commonLabelClasses}
              label={`${t('MA_OH_ORD_TOTAL_TAX')}:`}
              valueClasses={commonValueClasses}
              value={priceDisplayFormat({ price: tax, currency })}
            />
          )}
          {taxLabel &&
            taxLabel.map(({ label, value }) => (
              <SummaryOrderTotalsRow
                key={label}
                labelClasses={['ps-4', ...commonLabelClasses]}
                label={label}
                valueClasses={commonValueClasses}
                value={priceDisplayFormat({ price: value, currency })}
              />
            ))}
          {showOrderTotal && (
            <>
              <SummaryOrderTotalsRow
                labelClasses={['text-uppercase', ...commonLabelClasses]}
                label={
                  showInvoiceLabels
                    ? `${t('INVOICE_TOTAL')}:`
                    : `${t('EMAIL_ORDER_TOTAL')}:`
                }
                valueClasses={[
                  'fw-normal text-univers--bold',
                  ...commonValueClasses
                ]}
                value={priceDisplayFormat({ price: total, currency })}
              />
              <Conditional test={showCatGlobalOrderTotal}>
                <div className="d-flex justify-content-end">
                  <p
                    className="mb-0 u-fs--12"
                    data-testid="dual-currency-order-total"
                  >
                    {`(${secondaryOrderTotals} ${orderTotalSecondaryCurrencyCode})`}
                  </p>
                </div>
              </Conditional>
            </>
          )}
          <br />

          <Conditional test={showCatCredit}>
            <>
              <SummaryOrderTotalsRow
                labelClasses={[
                  {
                    'mt-3': isOrderConfirmation || isCatCreditInternational
                  },
                  ...commonLabelClasses
                ]}
                label={catCreditsLabel}
                valueClasses={[
                  'fw-normal',
                  { 'mt-3': isOrderConfirmation || isCatCreditInternational },
                  ...commonValueClasses
                ]}
                value={
                  isDualCurrencySupportFlagEnabled &&
                  shouldShowDualAppliedCredits
                    ? amountUsedFormatted
                    : catCreditsTotalFormatted
                }
              />
              <Conditional
                test={
                  isDualCurrencySupportFlagEnabled &&
                  shouldShowDualAppliedCredits
                }
              >
                <div className="d-flex justify-content-end">
                  <p
                    className="mb-0 u-fs--12"
                    data-testid="dual-currency-credits"
                  >
                    {`($${secondaryAmountUsedFixed} ${currencyCode})`}
                  </p>
                </div>
              </Conditional>

              <Conditional
                test={
                  isIntlCatCreditsEnhancementsPhase1FlagEnabled &&
                  isDualCurrencySupportFlagEnabled &&
                  (isOrderConfirmation || isOrderHistoryDetail) &&
                  catCreditsSalesforceAmount
                }
              >
                <div className="d-flex justify-content-end">
                  <p
                    className="mb-0 u-fs--12"
                    data-testid="cat-credits-order-confirmation-history"
                  >
                    {`(${catCreditsSalesforceAmount} ${salesforceCurrency})`}
                  </p>
                </div>
              </Conditional>
            </>
          </Conditional>

          <Conditional test={showBillingMethodDifferentToCatCredits}>
            <SummaryOrderTotalsRow
              labelClasses={['text-uppercase', ...commonLabelClasses]}
              label={`${billingMethod}:`}
              valueClasses={['text-univers--bold', ...commonValueClasses]}
              value={priceDisplayFormat({
                price: orderRemainingPaymentAmount ?? 0,
                currency
              })}
            />
          </Conditional>

          <Conditional
            test={
              isIntlCatCreditsEnhancementsPhase1FlagEnabled &&
              isCatCardBillingMethod &&
              isDualCurrencySupportFlagEnabled &&
              (isOrderConfirmation || isOrderHistoryDetail) &&
              catCardSalesforceAmount
            }
          >
            <div className="d-flex justify-content-end">
              <p
                className="mb-0 u-fs--12"
                data-testid="cat-card-order-confirmation-history"
              >
                {`(${catCardSalesforceAmount} ${salesforceCurrency})`}
              </p>
            </div>
          </Conditional>

          <Conditional
            test={
              showCatCredit && !isOrderConfirmation && !isOrderHistoryDetail
            }
          >
            <>
              <SummaryOrderTotalsRow
                labelClasses={['text-uppercase', ...commonLabelClasses]}
                label={
                  isOrderHistoryDetail ? billingMethod : t('AMOUNT_REMAINING')
                }
                valueClasses={[
                  'fw-normal text-univers--bold',
                  ...commonValueClasses
                ]}
                value={
                  isDualCurrencySupportFlagEnabled && secondaryRemainingAmount
                    ? `$${remainingAmountToShow} ${currency}`
                    : `${orderRemainingPaymentAmount ?? 0} ${currency}`
                }
              />
              <Conditional
                test={
                  isDualCurrencySupportFlagEnabled && secondaryRemainingAmount
                }
              >
                <div className="d-flex justify-content-end">
                  <p
                    className="mb-0 u-fs--12"
                    data-testid="dual-currency-remaining"
                  >
                    {`($${secondaryRemainingAmountToShow} ${currencyCode})`}
                  </p>
                </div>
              </Conditional>
            </>
          </Conditional>

          {zeroFreightMessageEnabled && (
            <tr>
              <td className={cx(printTextStyle, chargedCustomClass)}>{`*${t(
                'CAT_DEALER_SHIPPING_TAX_PREFERENCES'
              )}`}</td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
};

SummaryOrderTotals.propTypes = {
  currency: PropTypes.string,
  discount: PropTypes.string,
  subtotal: PropTypes.string,
  catCreditTotal: PropTypes.string,
  orderRemainingPaymentAmount: PropTypes.string,
  shippingCost: PropTypes.string,
  tax: PropTypes.string,
  orderAdjustment: PropTypes.string,
  chargedCustomClass: PropTypes.string,
  taxLabel: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ),
  customLabelClassName: PropTypes.arrayOf(PropTypes.string),
  customValueClassName: PropTypes.arrayOf(PropTypes.string),
  total: PropTypes.string,
  vatTaxEnabled: PropTypes.bool,
  zeroFreightMessageEnabled: PropTypes.bool,
  isOrderHistoryDetail: PropTypes.bool,
  billingMethod: PropTypes.string,
  additionalBillingMethod: PropTypes.string,
  isOrderConfirmation: PropTypes.bool,
  showBillingMethods: PropTypes.bool,
  miscellaneous: PropTypes.string,
  miscellaneousItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ),
  showInvoiceLabels: PropTypes.bool,
  showOrderTotal: PropTypes.bool,
  catCreditTotalUnformatted: PropTypes.number,
  catCreditsSalesforceAmount: PropTypes.string,
  salesforceCurrency: PropTypes.string,
  catCardSalesforceAmount: PropTypes.string,
  billingMethodValue: PropTypes.string
};

export default SummaryOrderTotals;
